<script lang="ts">
export interface DrawerTab {
  key: string;
  icon: string;
  /** Mis en avant sans être ouvert (ex. outil Bloc actif → onglet Blocs). */
  highlighted: boolean;
}
</script>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from "vue";
import { Facade } from "@/drawing";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { isCanvasDrawing } from "@/stores/CanvasViewState";
import { Z_INDEX_DRAWER } from "./constants";
import {
  canvasTopForIcons,
  DRAWER_TAB_SIZE,
  DRAWER_TABS_GAP,
  DRAWER_TABS_TOP_GAP,
  drawerTabsRight,
  verticalRulerRightOffset,
} from "@/stores/CanvasLayoutState";

/**
 * Onglets des volets en bulles flottantes, en haut à droite du plan. Le volet
 * ouvert les pousse vers la gauche : elles restent collées à son bord.
 */

defineProps<{
  tabs: DrawerTab[];
  activeKey: string | null;
  /** Le volet est en cours de redimensionnement : les bulles le suivent sans délai. */
  resizing: boolean;
}>();

const emit = defineEmits<{ (e: "toggle", key: string): void }>();

// Règle verticale collée au bord droit : les bulles s'en écartent
// (cf. drawerTabsRight).
onMounted(() => {
  if (!Facade.isInitialized) return;
  Facade.rulers.setVerticalRulerListener((offset) => {
    verticalRulerRightOffset.value = offset;
  });
});

onBeforeUnmount(() => {
  if (Facade.isInitialized) Facade.rulers.setVerticalRulerListener(null);
});
</script>

<template>
  <nav
    class="drawer-tabs canvas-icon-scaled"
    :class="{ resizing, 'drawing-active': isCanvasDrawing }"
    :aria-label="$t('canvas:drawers.tabs')"
    :style="{
      top: `${canvasTopForIcons + DRAWER_TABS_TOP_GAP}px`,
      gap: `${DRAWER_TABS_GAP}px`,
      right: `${drawerTabsRight}px`,
      zIndex: Z_INDEX_DRAWER,
    }"
  >
    <button
      v-for="tab in tabs"
      :key="tab.key"
      type="button"
      class="drawer-tab capture"
      :class="{
        active: activeKey === tab.key,
        highlighted: tab.highlighted && activeKey !== tab.key,
      }"
      :aria-pressed="activeKey === tab.key"
      :style="{
        width: `${DRAWER_TAB_SIZE}px`,
        height: `${DRAWER_TAB_SIZE}px`,
      }"
      @click="emit('toggle', tab.key)"
    >
      <PhosphorIcon :name="tab.icon" :size="20" />
      <span class="drawer-tab-label">{{
        $t(`canvas:settingsTabs.label.${tab.key}`)
      }}</span>
    </button>
  </nav>
</template>

<style lang="scss" scoped>
.drawer-tabs {
  position: absolute;
  display: flex;
  flex-direction: column;
  // Le volet s'ouvre en glissant : les bulles l'accompagnent.
  transition: right 0.12s ease;

  &.resizing {
    transition: none;
  }
}

.drawer-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 0 2px;
  border: 1px solid var(--canvas-border);
  border-radius: 12px;
  background-color: var(--canvas-bg);
  color: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(.active) {
    background-color: var(--canvas-bg-hover);
  }

  &:active {
    transform: scale(0.96);
  }

  &.active {
    background-color: var(--canvas-btn-active-bg);
    border-color: var(--canvas-btn-active-bg);
    color: #1a1a1a;
    box-shadow:
      0 4px 14px rgba(0, 0, 0, 0.45),
      0 2px 8px var(--canvas-btn-active-shadow);
  }

  &.highlighted {
    border-color: var(--canvas-btn-active-bg);
    box-shadow:
      0 4px 14px rgba(0, 0, 0, 0.45),
      0 0 0 2px var(--canvas-btn-active-shadow);
  }
}

.drawer-tab-label {
  max-width: 100%;
  overflow: hidden;
  font-size: 9.5px;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.85;
}
</style>
